import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface ChatMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  is_read: boolean;
  delivery_status: 'sending' | 'sent' | 'delivered' | 'read';
}

export interface TypingIndicator {
  user_id: string;
  conversation_id: string;
  is_typing: boolean;
}

export interface UserPresence {
  user_id: string;
  online_at: string;
  status: 'online' | 'away' | 'offline';
}

export class RealtimeChatService {
  private messageChannel: RealtimeChannel | null = null;
  private presenceChannel: RealtimeChannel | null = null;
  private typingChannel: RealtimeChannel | null = null;
  private connectionStatus: 'connecting' | 'connected' | 'disconnected' = 'disconnected';
  private messageQueue: ChatMessage[] = [];
  private currentUserId: string | null = null;

  // Event listeners
  private onMessageReceived: ((message: ChatMessage) => void) | null = null;
  private onTypingUpdate: ((typing: TypingIndicator) => void) | null = null;
  private onPresenceUpdate: ((presence: UserPresence[]) => void) | null = null;
  private onConnectionChange: ((status: string) => void) | null = null;

  constructor(userId?: string) {
    this.currentUserId = userId || null;
  }

  async connect(userId: string) {
    this.currentUserId = userId;
    this.connectionStatus = 'connecting';
    this.onConnectionChange?.(this.connectionStatus);

    try {
      // Message channel for real-time messages
      this.messageChannel = supabase
        .channel(`messages:${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          (payload) => {
            const message = payload.new as ChatMessage;
            if (message.recipient_id === userId || message.sender_id === userId) {
              this.onMessageReceived?.(message);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages'
          },
          (payload) => {
            const message = payload.new as ChatMessage;
            if (message.recipient_id === userId || message.sender_id === userId) {
              this.onMessageReceived?.(message);
            }
          }
        );

      // Presence channel for online status
      this.presenceChannel = supabase
        .channel('user_presence')
        .on('presence', { event: 'sync' }, () => {
          const state = this.presenceChannel?.presenceState();
          if (state) {
            const presenceList = Object.values(state).flat().map((p: any) => ({
              user_id: p.user_id,
              online_at: p.online_at,
              status: p.status
            })) as UserPresence[];
            this.onPresenceUpdate?.(presenceList);
          }
        })
        .on('presence', { event: 'join' }, ({ newPresences }) => {
          const presenceList = newPresences.map((p: any) => ({
            user_id: p.user_id,
            online_at: p.online_at,
            status: p.status
          })) as UserPresence[];
          this.onPresenceUpdate?.(presenceList);
        })
        .on('presence', { event: 'leave' }, ({ leftPresences }) => {
          const presenceList = leftPresences.map((p: any) => ({
            user_id: p.user_id,
            online_at: p.online_at,
            status: p.status
          })) as UserPresence[];
          this.onPresenceUpdate?.(presenceList);
        });

      // Typing indicators channel
      this.typingChannel = supabase
        .channel('typing_indicators')
        .on('broadcast', { event: 'typing' }, (payload) => {
          this.onTypingUpdate?.(payload.payload as TypingIndicator);
        });

      // Subscribe to all channels
      await Promise.all([
        this.messageChannel.subscribe(),
        this.presenceChannel.subscribe(),
        this.typingChannel.subscribe()
      ]);

      // Track user presence
      await this.presenceChannel.track({
        user_id: userId,
        online_at: new Date().toISOString(),
        status: 'online'
      });

      this.connectionStatus = 'connected';
      this.onConnectionChange?.(this.connectionStatus);

      // Process queued messages
      await this.processMessageQueue();

    } catch (error) {
      console.error('Failed to connect to realtime chat:', error);
      this.connectionStatus = 'disconnected';
      this.onConnectionChange?.(this.connectionStatus);
    }
  }

  async disconnect() {
    if (this.presenceChannel) {
      await this.presenceChannel.untrack();
    }

    await Promise.all([
      this.messageChannel?.unsubscribe(),
      this.presenceChannel?.unsubscribe(),
      this.typingChannel?.unsubscribe()
    ]);

    this.messageChannel = null;
    this.presenceChannel = null;
    this.typingChannel = null;
    this.connectionStatus = 'disconnected';
    this.onConnectionChange?.(this.connectionStatus);
  }

  async sendMessage(message: Omit<ChatMessage, 'id' | 'created_at' | 'delivery_status'>): Promise<void> {
    const messageWithStatus: ChatMessage = {
      ...message,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      delivery_status: 'sending'
    };

    if (this.connectionStatus !== 'connected') {
      // Queue message for later sending
      this.messageQueue.push(messageWithStatus);
      return;
    }

    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          sender_id: message.sender_id,
          recipient_id: message.recipient_id,
          content: message.content,
          is_read: message.is_read
        });

      if (error) throw error;

      messageWithStatus.delivery_status = 'sent';
      this.onMessageReceived?.(messageWithStatus);
    } catch (error) {
      console.error('Failed to send message:', error);
      messageWithStatus.delivery_status = 'sent'; // Fallback
      this.messageQueue.push(messageWithStatus);
    }
  }

  async markAsRead(messageId: string): Promise<void> {
    try {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  }

  async sendTypingIndicator(conversationId: string, isTyping: boolean): Promise<void> {
    if (!this.typingChannel || !this.currentUserId) return;

    await this.typingChannel.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        user_id: this.currentUserId,
        conversation_id: conversationId,
        is_typing: isTyping
      }
    });
  }

  private async processMessageQueue(): Promise<void> {
    while (this.messageQueue.length > 0 && this.connectionStatus === 'connected') {
      const message = this.messageQueue.shift();
      if (message) {
        try {
          await this.sendMessage({
            sender_id: message.sender_id,
            recipient_id: message.recipient_id,
            content: message.content,
            is_read: message.is_read
          });
        } catch (error) {
          console.error('Failed to process queued message:', error);
          // Re-queue the message
          this.messageQueue.unshift(message);
          break;
        }
      }
    }
  }

  // Event handlers
  onMessage(callback: (message: ChatMessage) => void) {
    this.onMessageReceived = callback;
  }

  onTyping(callback: (typing: TypingIndicator) => void) {
    this.onTypingUpdate = callback;
  }

  onPresence(callback: (presence: UserPresence[]) => void) {
    this.onPresenceUpdate = callback;
  }

  onConnection(callback: (status: string) => void) {
    this.onConnectionChange = callback;
  }

  getConnectionStatus(): string {
    return this.connectionStatus;
  }

  getQueuedMessageCount(): number {
    return this.messageQueue.length;
  }
}

// Singleton instance
let chatService: RealtimeChatService | null = null;

export const getChatService = (userId?: string): RealtimeChatService => {
  if (!chatService) {
    chatService = new RealtimeChatService(userId);
  }
  return chatService;
};